import { defineStore } from 'pinia'
import { useGAEAPI } from '@/composable/gae_api'
import { handleErrorNotification } from '@/services/service_api'
import type { NpsFormGET, NpsFormPOST, NpsResultDetailGET, NpsResultItem, NpsResultsGET } from 'types/api/nps'
import { useToast } from 'vue-toast-notification'

const toast = useToast()

export type NpsResultsOptions = {
  page?: number
  itemsPerPage?: number
  search?: string
  sort?: string
}

type NpsStoreState = {
  formData: NpsFormGET | null
  loading: boolean
  error: string | null
  submitting: boolean
  submitted: boolean
  results: NpsResultItem[]
  average: number | null
  totalItems: number
  loadingResults: boolean
  detail: NpsResultDetailGET | null
  loadingDetail: boolean
}

export const useNpsStore = defineStore('nps', {
  state: (): NpsStoreState => ({
    formData: null,
    loading: false,
    error: null,
    submitting: false,
    submitted: false,
    results: [],
    average: null,
    totalItems: 0,
    loadingResults: false,
    detail: null,
    loadingDetail: false,
  }),

  actions: {
    fetchForm(token: string) {
      const api = useGAEAPI()
      this.loading = true
      this.error = null
      return api.instance.get<NpsFormGET>(`/api/dynform/nps/${token}`)
        .then((response) => {
          this.formData = response.data
        })
        .catch((e) => {
          this.error = e.response?.status === 404
            ? 'Ce formulaire n\'existe pas ou a expiré.'
            : 'Une erreur est survenue. Veuillez réessayer.'
        })
        .finally(() => {
          this.loading = false
        })
    },

    submitForm(token: string, answers: NonNullable<NpsFormPOST['answers']>) {
      const api = useGAEAPI()
      this.submitting = true
      this.error = null
      return api.instance.post(`/api/dynform/nps/${token}`, { answers })
        .then(() => {
          toast.success('Vos réponses étés envoyées')
          this.submitted = true
        })
        .catch((e) => {
          this.error = e.response?.status === 409
            ? 'Ce formulaire a déjà été rempli.'
            : 'Une erreur est survenue lors de l\'envoi.'
        })
        .finally(() => {
          this.submitting = false
        })
    },

    fetchResults(options: NpsResultsOptions = {}) {
      const api = useGAEAPI()
      this.loadingResults = true
      return api.instance.get<NpsResultsGET>('/api/nps_results', {
        headers: { Accept: 'application/json' },
        params: options,
      })
        .then((response) => {
          this.results = response.data.items
          this.average = response.data.average
          this.totalItems = response.data.totalItems
        })
        .catch(handleErrorNotification)
        .finally(() => {
          this.loadingResults = false
        })
    },

    fetchDetail(interventionId: number) {
      const api = useGAEAPI()
      this.loadingDetail = true
      this.detail = null
      return api.instance.get<NpsResultDetailGET>(`/api/nps_results/${interventionId}`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          this.detail = response.data
        })
        .catch(handleErrorNotification)
        .finally(() => {
          this.loadingDetail = false
        })
    },

    exportCsv(search?: string, sort?: string) {
      const api = useGAEAPI()
      return api.instance.get<NpsResultsGET>('/api/nps_results', {
        headers: { Accept: 'application/json' },
        params: { itemsPerPage: 100000, search, sort },
      })
        .then((response) => {
          const escape = (value: string) => `"${value.replace(/"/g, '""')}"`
          const rows = response.data.items.map((item) => [
            new Date(item.date).toLocaleDateString('fr-FR'),
            escape(item.customer ?? ''),
            escape(item.backer ?? ''),
            item.score !== null ? String(item.score).replace('.', ',') : '',
            escape(item.comments.map((c) => `${c.label} : ${c.value}`).join('\n')),
          ].join(';'))
          const csv = '\ufeff' + ['Date;Bénéficiaire;Commanditaire;Note;Commentaires', ...rows].join('\r\n')

          const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'notes-nps.csv')
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
        .catch(handleErrorNotification)
    },
  },
})
